export type Quat = [number, number, number, number]; // [w, x, y, z]

export function quatInverse(q: Quat): Quat { return [q[0], -q[1], -q[2], -q[3]]; }

export function quatMultiply(a: Quat, b: Quat): Quat {
  return [
    a[0]*b[0] - a[1]*b[1] - a[2]*b[2] - a[3]*b[3],
    a[0]*b[1] + a[1]*b[0] + a[2]*b[3] - a[3]*b[2],
    a[0]*b[2] - a[1]*b[3] + a[2]*b[0] + a[3]*b[1],
    a[0]*b[3] + a[1]*b[2] - a[2]*b[1] + a[3]*b[0],
  ];
}

export function quatNormalize(q: Quat): Quat {
  const len = Math.sqrt(q[0]*q[0] + q[1]*q[1] + q[2]*q[2] + q[3]*q[3]);
  if (len < 1e-8) return [1, 0, 0, 0];
  return [q[0]/len, q[1]/len, q[2]/len, q[3]/len];
}

export function quatSlerp(a: Quat, b: Quat, t: number): Quat {
  let dot = a[0]*b[0] + a[1]*b[1] + a[2]*b[2] + a[3]*b[3];
  let b2: Quat = b;
  if (dot < 0) { b2 = [-b[0], -b[1], -b[2], -b[3]]; dot = -dot; }
  if (dot > 0.9995) {
    return quatNormalize([a[0]+(b2[0]-a[0])*t, a[1]+(b2[1]-a[1])*t, a[2]+(b2[2]-a[2])*t, a[3]+(b2[3]-a[3])*t]);
  }
  const theta = Math.acos(dot), sinT = Math.sin(theta);
  const wa = Math.sin((1-t)*theta)/sinT, wb = Math.sin(t*theta)/sinT;
  return [a[0]*wa+b2[0]*wb, a[1]*wa+b2[1]*wb, a[2]*wa+b2[2]*wb, a[3]*wa+b2[3]*wb];
}

export function quatToDeviceRotation(q: Quat): { roll: number; pitch: number; yaw: number } {
  const [w, x, y, z] = q;
  const roll = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
  const pitch = Math.atan2(2*(w*x + y*z), 1 - 2*(x*x + y*y));
  const sinYaw = 2*(w*y - z*x);
  const yaw = Math.abs(sinYaw) >= 1 ? Math.sign(sinYaw) * Math.PI/2 : Math.asin(sinYaw);
  return { roll, pitch, yaw };
}

export interface LoopQuat { w: number; x: number; y: number; z: number; }

export function loopQuatToArray(q: LoopQuat): Quat { return [q.w, q.x, q.y, q.z]; }
